/**
 * Energy Certificate Simulator - Energy Coefficients
 *
 * Primary energy coefficients (wi) and system efficiencies (η)
 * based on Polish regulations: Dz.U. 2023 poz. 697
 */

import type { HeatingSystem, SystemAge, HotWaterSource } from '../types';

// =============================================================================
// PRIMARY ENERGY COEFFICIENTS (wi)
// =============================================================================

/**
 * Primary energy coefficient by energy carrier
 * Source: Dz.U. 2023 poz. 697 - Załącznik nr 4
 */
export const PRIMARY_ENERGY_COEFFICIENTS: Record<string, number> = {
  electricity: 2.5,
  natural_gas: 1.1,
  coal: 1.1,
  heating_oil: 1.1,
  biomass: 0.2, // Wood, pellets
  district_heating_standard: 1.0,
  district_heating_high_efficiency: 0.8,
  district_heating_cogeneration: 0.7,
} as const;

/**
 * Get primary energy coefficient for a heating system
 */
export function getPrimaryEnergyCoefficient(heatingSystem: HeatingSystem): number {
  switch (heatingSystem) {
    case 'gas_condensing':
    case 'gas_traditional':
      return PRIMARY_ENERGY_COEFFICIENTS.natural_gas;
    case 'coal':
    case 'stove':
      return PRIMARY_ENERGY_COEFFICIENTS.coal;
    case 'biomass':
      return PRIMARY_ENERGY_COEFFICIENTS.biomass;
    case 'heat_pump_air':
    case 'heat_pump_ground':
    case 'electric_radiators':
    case 'electric_floor':
      return PRIMARY_ENERGY_COEFFICIENTS.electricity;
    case 'district_heating':
      return PRIMARY_ENERGY_COEFFICIENTS.district_heating_standard;
    default:
      return PRIMARY_ENERGY_COEFFICIENTS.natural_gas;
  }
}

// =============================================================================
// SYSTEM EFFICIENCIES (η)
// =============================================================================

/**
 * Heating system efficiency by system type and age
 * Values represent total system efficiency including:
 * - Generation efficiency
 * - Distribution efficiency
 * - Regulation efficiency
 */
export const SYSTEM_EFFICIENCIES: Record<HeatingSystem, Record<SystemAge, number>> = {
  gas_condensing: { new: 0.97, medium: 0.92, old: 0.85 },
  gas_traditional: { new: 0.90, medium: 0.82, old: 0.75 },
  coal: { new: 0.80, medium: 0.70, old: 0.60 },
  biomass: { new: 0.85, medium: 0.78, old: 0.70 },
  heat_pump_air: { new: 3.5, medium: 3.2, old: 2.8 }, // COP values
  heat_pump_ground: { new: 4.5, medium: 4.0, old: 3.5 }, // COP values
  electric_radiators: { new: 1.0, medium: 1.0, old: 1.0 },
  electric_floor: { new: 1.0, medium: 1.0, old: 1.0 },
  district_heating: { new: 0.95, medium: 0.90, old: 0.85 },
  stove: { new: 0.75, medium: 0.65, old: 0.55 },
};

/**
 * Get system efficiency for a heating system and age
 */
export function getSystemEfficiency(
  heatingSystem: HeatingSystem,
  systemAge: SystemAge
): number {
  return SYSTEM_EFFICIENCIES[heatingSystem]?.[systemAge] ?? 0.8;
}

/**
 * Check if heating system is a heat pump (uses COP instead of η)
 */
export function isHeatPump(heatingSystem: HeatingSystem): boolean {
  return heatingSystem === 'heat_pump_air' || heatingSystem === 'heat_pump_ground';
}

// =============================================================================
// HOT WATER SYSTEM EFFICIENCIES
// =============================================================================

/**
 * Hot water system efficiency by source
 */
export const HOT_WATER_EFFICIENCIES: Record<HotWaterSource, number> = {
  combined: 0.85, // Combined with central heating
  electric: 0.95, // Electric water heater
  gas: 0.85, // Gas water heater
  solar: 0.50, // Solar covers ~50% of demand
};

/**
 * Get hot water system efficiency
 */
export function getHotWaterEfficiency(hotWaterSource: HotWaterSource): number {
  return HOT_WATER_EFFICIENCIES[hotWaterSource] ?? 0.85;
}

/**
 * Get primary energy coefficient for hot water system
 */
export function getHotWaterPrimaryCoefficient(hotWaterSource: HotWaterSource): number {
  switch (hotWaterSource) {
    case 'electric':
      return PRIMARY_ENERGY_COEFFICIENTS.electricity;
    case 'gas':
      return PRIMARY_ENERGY_COEFFICIENTS.natural_gas;
    case 'solar':
      return PRIMARY_ENERGY_COEFFICIENTS.electricity * 0.5; // Partial coverage
    case 'combined':
    default:
      return PRIMARY_ENERGY_COEFFICIENTS.natural_gas; // Default to gas
  }
}

// =============================================================================
// CLIMATE DATA (POLAND)
// =============================================================================

/**
 * Climate constants for Poland (zone III - central)
 */
export const POLAND_CLIMATE = {
  /** Indoor design temperature [°C] */
  indoorTemp: 20,
  /** Average outdoor temperature during heating season [°C] */
  avgOutdoorTemp: 4.5,
  /** Heating season length [days] */
  heatingSeasonDays: 220,
  /** Heating season length [hours] */
  heatingSeasonHours: 220 * 24,
  /** Heating degree days [K·d/year] */
  heatingDegreeDays: 3600,
  /** Cold water temperature [°C] */
  coldWaterTemp: 10,
  /** Hot water temperature [°C] */
  hotWaterTemp: 55,
} as const;

/**
 * Regional climate data per Polish climate zone (PN-EN 12831)
 * Source: Typical meteorological years from IMGW data
 */
export const CLIMATE_ZONES = {
  I: {
    indoorTemp: 20,
    avgOutdoorTemp: 5.5,
    heatingSeasonDays: 200,
    heatingSeasonHours: 200 * 24,
    heatingDegreeDays: 3200,
    coldWaterTemp: 10,
    hotWaterTemp: 55,
    label: 'Strefa I (najcieplejsza)',
    description: 'Szczecin, Wrocław, Zielona Góra',
  },
  II: {
    indoorTemp: 20,
    avgOutdoorTemp: 5.0,
    heatingSeasonDays: 210,
    heatingSeasonHours: 210 * 24,
    heatingDegreeDays: 3400,
    coldWaterTemp: 10,
    hotWaterTemp: 55,
    label: 'Strefa II',
    description: 'Poznań, Łódź, Kraków',
  },
  III: {
    indoorTemp: 20,
    avgOutdoorTemp: 4.5,
    heatingSeasonDays: 220,
    heatingSeasonHours: 220 * 24,
    heatingDegreeDays: 3600,
    coldWaterTemp: 10,
    hotWaterTemp: 55,
    label: 'Strefa III (domyślna)',
    description: 'Warszawa, Lublin, Rzeszów',
  },
  IV: {
    indoorTemp: 20,
    avgOutdoorTemp: 3.5,
    heatingSeasonDays: 232,
    heatingSeasonHours: 232 * 24,
    heatingDegreeDays: 3900,
    coldWaterTemp: 10,
    hotWaterTemp: 55,
    label: 'Strefa IV',
    description: 'Olsztyn, Białystok, Kielce',
  },
  V: {
    indoorTemp: 20,
    avgOutdoorTemp: 2.5,
    heatingSeasonDays: 245,
    heatingSeasonHours: 245 * 24,
    heatingDegreeDays: 4200,
    coldWaterTemp: 10,
    hotWaterTemp: 55,
    label: 'Strefa V (najzimniejsza)',
    description: 'Suwałki, Zakopane, rejony górskie',
  },
} as const;

// =============================================================================
// VENTILATION PARAMETERS
// =============================================================================

/**
 * Air change rate [1/h] for residential buildings
 */
export const AIR_CHANGE_RATES = {
  /** Natural ventilation without recuperation */
  natural: 0.5,
  /** With recuperation (heat recovery ventilation) */
  recuperation: 0.35,
  /** Mechanical supply ventilation */
  mechanical: 0.4,
} as const;

/**
 * Recuperation heat recovery efficiency
 */
export const RECUPERATION_EFFICIENCY = 0.85;

// =============================================================================
// RENEWABLE ENERGY PARAMETERS
// =============================================================================

/**
 * Photovoltaic production parameters for Poland
 */
export const PV_PARAMETERS = {
  /** Annual production per kWp [kWh/kWp/year] */
  annualProductionPerKwp: 950,
  /** Maximum EP reduction from PV (70% limit) */
  maxEPReductionFactor: 0.7,
  /** Self-consumption factor (energy used directly) */
  selfConsumptionFactor: 0.5,
} as const;

/**
 * Solar thermal parameters
 */
export const SOLAR_THERMAL_PARAMETERS = {
  /** Typical hot water demand coverage [%] */
  hotWaterCoverage: 0.6,
  /** Efficiency factor */
  efficiency: 0.5,
} as const;
